import { getBaseUrl } from "@/lib/utils";
import { removeSegments } from "@frend-digital/ecom-layer/seo";
import {
	defineRouting,
	parseGeo,
	replaceLocale,
	type GeoLocale,
} from "@frend-digital/next/i18n";
import { defineNavigation } from "@frend-digital/next/i18n/navigation";
import { addLeadingSlash, dedupeArray, removeTrailingSlash } from "@frend-digital/utils";
import { Result } from "@frend-digital/utils/results";
import { countryCodes } from "../../generated/countries";

export const LANGUAGES = ["en", "no"];

export const localesWithCountries = LANGUAGES.flatMap((locale) => {
	return countryCodes.map((country) => `${locale}-${country.country}`);
});

export const sitemapLocales = dedupeArray([
	...countryCodes.map((country) => `${country.language}-${country.country}`),
	...localesWithCountries.filter((c) => c.includes("en-")),
]);

export const routing = defineRouting({
	languages: LANGUAGES,
	defaultLocale: "no-NO",
	locales: localesWithCountries,
	localePrefix: "always",
	/**
	 * Sets the preferred countries when multiple countries are available for a language
	 */
	domain: getBaseUrl(),
	preferredCountries: ["NO"],
	groups: ["categories", "products", "pages"],
	removeSegments: ["collections", "products", "pages", "blogs", "home", ...LANGUAGES],
});

export const countries = countryCodes;

export const {
	Link,
	Form,
	permanentRedirect,
	redirect,
	usePathname,
	useRouter,
	useGeo,
	SearchParamLink,
	addLocale,
} = defineNavigation(routing);

export function findGeoByLanguage(
	language: string,
	preferredCountries: string[],
): string | undefined {
	const sortedCountries = countries.sort((a, b) => {
		const indexA = preferredCountries.indexOf(a.country);
		const indexB = preferredCountries.indexOf(b.country);

		// If a country is in the preferred list, it gets a higher priority
		if (indexA !== -1 && indexB !== -1) {
			return indexA - indexB; // Sort based on order in preferredCountries
		}
		if (indexA !== -1) {
			return -1; // a is preferred, so it comes first
		}
		if (indexB !== -1) {
			return 1; // b is preferred, so it comes first
		}
		return 0; // Neither is preferred, keep the original order
	});

	const country = sortedCountries.find((c) => c.language === language);
	if (!country) return undefined;
	return `${language}-${country?.country}`;
}

export const findCountry = (country: string) => {
	return countryCodes.find((c) => c.country.toLowerCase() === country.toLowerCase());
};

export const withDomain = (locale: string, uri: string) => {
	return `${routing.domain}${addLeadingSlash(
		removeTrailingSlash(replaceLocale(uri, locale || routing.defaultLocale, routing)),
	)}`;
};

export const countryData = (country: string) => {
	const foundCountry = countries.find((c) => c.country === country);
	if (!foundCountry) return;

	return {
		...foundCountry,
		pricelist: Number.parseInt(foundCountry.pricelist),
		market: Number.parseInt(foundCountry.market),
	};
};

export const getCountry = (geo: GeoLocale) => {
	const { country, language } = Result.unwrap(parseGeo(geo));

	const data = countryData(country);
	if (!data) {
		throw new Error("No country data found");
	}

	const { market, pricelist } = data;

	return {
		market,
		pricelist,
		language,
		country,
	};
};

export const getFinalUrl = (locale: string, path: string) => {
	return withDomain(locale, removeSegments(path, routing.removeSegments));
};
